import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaSignInAlt } from 'react-icons/fa';
import { Link } from 'react-router';
import { z } from 'zod';
import RHFInput from './RHF/RHFInput';
import { RHFSubmitButton } from './RHF/RHFSubmitButton';
import { toast } from 'react-toastify';
import AuthApi from '../apis/AuthApi';
import { handleError } from '../apis';

const resetSchema = z
  .object({
    code: z.string(),
    email: z.email('Required!').trim().min(1, { message: 'Required!' }),
    password: z
      .string('Required!')
      .trim()
      .min(6, { message: 'Password must be at least 6 characters long' })
      .refine(
        (value) => /[a-z]/.test(value),
        'Password must contain a lowercase character',
      )
      .refine(
        (value) => /[A-Z]/.test(value),
        'Password must contain a uppercase character',
      )
      .refine(
        (value) => /[!@#$%^&*]/.test(value),
        'Password must contain a special character (!@#$%^&*)',
      )
      .refine((value) => /\d/.test(value), 'Password must contain a number'),
    confirmPassword: z.string('Required!').trim(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'], // path of error
  });

type ResetSchema = z.infer<typeof resetSchema>;

export default function ResetPasswordForm() {
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const code = new URLSearchParams(window.location.search).get('code') || '';
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<ResetSchema>({
    defaultValues: {
      code: code,
    },
    resolver: zodResolver(resetSchema),
  });

  useEffect(() => {
    if (!code) {
      toast.error('Invalid password reset link');
    }
  }, [code]);
  const handleReset = async (values: ResetSchema) => {
    try {
      const response = await AuthApi.resetPassword(values);
      if (response.message === 'Success') {
        setIsSuccess(true);
      } else {
        toast.error('Something went wrong');
      }
    } catch (error) {
      const message = await handleError(error);
      toast.error(message);
    }
  };

  return (
    <div
      className='my-4 flex w-[320px] flex-initial flex-col items-center justify-start self-auto rounded-2xl border border-gray-500 p-3 dark:border-gray-500 '
      id='forgot-form'
    >
      {isSuccess ? (
        <>
          <h3>Password Reset Successful</h3>
          <Link to='/' className='font-bold'>
            <div className='flex items-center gap-2'>
              Login <FaSignInAlt />
            </div>
          </Link>
        </>
      ) : (
        <>
          <h3 className='mb-2'>Reset Password</h3>
          <form className='w-full' onSubmit={handleSubmit(handleReset)}>
            {/* <RHFHiddenInput name='code' type='text' control={control} /> */}
            <RHFInput
              name='email'
              label='Email'
              type='text'
              register={register('email')}
              errors={errors}
            />
            <RHFInput
              name='password'
              label='Password'
              type='password'
              register={register('password')}
              errors={errors}
            />
            <RHFInput
              name='confirmPassword'
              label='Confirm Password'
              type='password'
              register={register('confirmPassword')}
              errors={errors}
            />
            <RHFSubmitButton isSubmitting={isSubmitting} />
          </form>
          <Link to='/' className='text-xs'>
            or Login
          </Link>
        </>
      )}
    </div>
  );
}
